import type { z } from 'zod';

import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';
import { type AdCopySlot, applyVariantEdit } from '@/lib/briefs/ad-copy-edit';
import {
  GoogleRsaAdUnitCopyReadSchema,
  LinkedInAdUnitCopyReadSchema,
  MetaAdUnitCopyReadSchema,
} from '@/lib/briefs/ad-schemas';
import { logger } from '@/utils/logger';

/**
 * Read model over `ad_units` — the persisted output of `generate_ad_copy`.
 * Identity is the resolved account owner; visibility is the exact workspace.
 * Both are required. An owner-wide read is a cross-client leak, not a library.
 */

export type AdUnitStatus = 'draft' | 'approved' | 'rejected' | 'exported';
export type AdUnitPlatform = 'meta' | 'linkedin' | 'google_rsa';

/**
 * `meta` is the only platform that carries both a still and a moving
 * placement under the SAME `ad_platform` value, so this is the one signal
 * that tells two Meta rows created by "Generate content" apart — one meant
 * to carry a still image, one meant to carry Veo output. `linkedin` and
 * `google_rsa` rows are always `'image'`: LinkedIn's fan-out combo is
 * image-only by this feature's own scope, and Google RSA has no creative
 * slot at all by construction (see `adUnitCompleteness` in
 * `BriefConceptAdUnits.tsx`) — `'image'` there is a harmless default, never
 * read as "this RSA has an image."
 */
export type AdUnitFormat = 'image' | 'video';

export interface AdUnitWarning {
  field: string;
  detail: string;
  provenance: string;
}

/**
 * `ad_units.copy` is PROVIDER-SHAPED — Meta, LinkedIn and Google each store
 * different fields — and `AdUnitRecord` used to describe only the Meta shape
 * for every platform. That was not a cosmetic inaccuracy: `BriefConceptAdUnits`
 * called `copy.primaryTexts.map(...)` on whatever it was handed, so the first
 * LinkedIn row would have taken the concept panel down, and the type system
 * would have reported no problem at all.
 *
 * The fix is a discriminated union keyed on `adPlatform`, so a reader cannot
 * dereference Meta fields without first proving it holds a Meta row.
 */
export type MetaAdUnitCopy = z.infer<typeof MetaAdUnitCopyReadSchema>;
export type LinkedInAdUnitCopy = z.infer<typeof LinkedInAdUnitCopyReadSchema>;
export type GoogleRsaAdUnitCopy = z.infer<typeof GoogleRsaAdUnitCopyReadSchema>;

export interface AdUnitCopyByPlatform {
  meta: MetaAdUnitCopy;
  linkedin: LinkedInAdUnitCopy;
  google_rsa: GoogleRsaAdUnitCopy;
}

export interface AdUnitGrounding {
  groundedIn: string[];
  notGroundedIn: string[];
  summary: string;
}

/**
 * The "Generate content" fan-out's OWN sibling ledger — a deliberate,
 * documented substitute for `ad_units.concept_copy_command_id` /
 * `concept_copy_position`, not a duplicate of them.
 *
 * Those two columns ARE the real sibling ledger this feature conceptually
 * belongs to, and reading them (`BriefConceptAdUnits`'s existing "sibling
 * copy variants" grouping) needed no change. Writing them is a different
 * story: `authenticated` was granted INSERT on both for six migration-hours
 * (`20260816194000_grant_concept_copy_lineage_insert.sql`) and had it
 * revoked the same day (`20260816210000_revoke_concept_copy_lineage_authenticated_insert.sql`),
 * whose own header calls the pair "server-authored" — written only by
 * `POST /api/concepts/copy-variants`'s service-role client. Neither column
 * has ever carried an UPDATE grant either (checked against every
 * `supabase/migrations/*.sql` "grant update … ad_units" statement). This
 * feature is a client-only surface with no service-role write path of its
 * own — building one would mean a new `server/api/*` route, out of this
 * change's file boundary — so it cannot use those columns.
 *
 * `copy` IS writable end-to-end for `authenticated` (present in every
 * INSERT/UPDATE grant list `ad_units` has ever had), so batch identity rides
 * inside the same per-row JSON the platform copy already lives in, in its
 * own namespace — a real, round-tripping value on every row it is written
 * to, not a fabricated one, and structurally the same move `conceptCopy`
 * (the concept-workbench's own anchor metadata, `src/lib/concept-copy/persistence.ts`)
 * already makes for the identical reason. Promote this to
 * `concept_copy_command_id` if/when a service-role fan-out endpoint exists.
 */
export interface AdUnitFanOut {
  /** Client-generated UUID shared by every row one "Generate content" action creates. */
  batchId: string;
  /** 0-based position within the batch, in creation order. */
  position: number;
  format: AdUnitFormat;
}

interface AdUnitRecordBase {
  id: string;
  brandKitId: string;
  briefId: string | null;
  projectTaskId: string | null;
  product: string;
  offerClass: string;
  status: AdUnitStatus;
  grounding: AdUnitGrounding;
  /**
   * Advisory notes, read from the `warnings` COLUMN.
   *
   * This used to be read off `copy.warnings`, which was true of rows written
   * before 2026-08-11 and false of every row since — the writer moved warnings
   * out of the copy payload into their own column and the reader was never
   * updated. Verified in prod on 2026-08-12: 10 of 12 rows carry them inside
   * `copy`, 2 do not, and the column is populated on all 12. So the old read
   * silently showed nothing on the newest rows while looking perfectly healthy.
   * The column is the source of truth; the legacy in-copy array is merged in
   * as a fallback so historical rows keep rendering.
   */
  warnings: AdUnitWarning[];
  /**
   * The `brand_audiences` row this ad copy was written for, or null.
   *
   * Null is the honest answer for every row in production today — nothing in
   * the generation path sets it yet — and a reader must render that as "no
   * audience" rather than borrowing the enclosing Concept's persona, which
   * would assert a binding the database does not hold.
   */
  audienceId: string | null;
  /**
   * Author-written statement of what this ad is testing (`hypothesis` column).
   *
   * Never generated. Null means nobody has recorded one, which is a real and
   * displayable state — the point of the field is that an ad with no measured
   * evidence behind it says so.
   */
  hypothesis: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewNote: string | null;
  createdAt: string;
  /**
   * The competitor ad this copy was adapted from via "Write my own version"
   * on `/discover/ads` (`reference_ad_*` columns, migration
   * `20260818030349_ad_units_reference_ad_provenance.sql`). `null` when this
   * copy was not started from a competitor ad — the majority case — and that
   * is a real, displayable state, not a loading gap.
   */
  referenceAd: { id: string; pageId: string | null; advertiser: string | null } | null;
  /**
   * The Concept holding this ad's creative (`concept_rendition_sets.id`, via
   * `ad_units.concept_creative_id` — migration
   * `20260818223000_ad_units_concept_creative_link.sql`). Null means no
   * creative is attached yet — the majority case today — which is a real,
   * displayable state ("no creative yet"), not a loading gap. Points at the
   * whole Concept (all aspect ratios), never a single `concept_renditions`
   * row: an ad's creative is exported per-ratio at composite/launch time.
   */
  conceptCreativeId: string | null;
  /**
   * `ad_units.video_job_id` → the `video_jobs` row holding this ad's moving
   * creative (migration `20260828020000_ad_units_video_job_link.sql`). Null
   * is a real, displayable state — "no video attached yet" — not a loading
   * gap, the same convention `conceptCreativeId` above uses for stills.
   */
  videoJobId: string | null;
  /**
   * Set only on rows created by the "Generate content" fan-out; null for
   * every row written by any other path (chat, the concept-copy workbench,
   * "Write my own version", or a row written before this feature existed).
   * See `AdUnitFanOut`'s own doc comment for why this is not
   * `concept_copy_command_id`.
   */
  fanOut: AdUnitFanOut | null;
}

/**
 * `copy` is null when the stored payload did not match its platform's shape.
 * An unreadable row renders as an explicit "couldn't read this" rather than
 * either crashing the panel or being silently dropped from the list — the
 * caller is forced to decide, because the type will not let it dereference.
 */
export type AdUnitRecord = {
  [P in AdUnitPlatform]: AdUnitRecordBase & {
    adPlatform: P;
    copy: AdUnitCopyByPlatform[P] | null;
  };
}[AdUnitPlatform];

export interface AdUnitRow {
  id: string;
  brand_kit_id: string;
  brief_id: string | null;
  project_task_id: string | null;
  product: string;
  ad_platform: string;
  offer_class: string;
  status: string;
  copy: unknown;
  grounding: unknown;
  warnings: unknown;
  audience_id: string | null;
  hypothesis: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_note: string | null;
  created_at: string;
  reference_ad_id: string | null;
  reference_ad_page_id: string | null;
  reference_ad_advertiser: string | null;
  concept_creative_id: string | null;
  video_job_id: string | null;
}

export interface ReviewAdUnitInput {
  adUnitId: string;
  status: Extract<AdUnitStatus, 'approved' | 'rejected'>;
  reviewNote?: string;
}

const COPY_READ_SCHEMAS = {
  meta: MetaAdUnitCopyReadSchema,
  linkedin: LinkedInAdUnitCopyReadSchema,
  google_rsa: GoogleRsaAdUnitCopyReadSchema,
} as const;

/**
 * Parse, rather than assert, the stored copy against its platform's shape.
 *
 * The reader schemas are intentionally lenient (see their doc comment in
 * `ad-schemas.ts`): the writer's `Persisted*` schemas are `.strict()` and would
 * reject the ten live rows that still carry `offerClass`/`warnings` inside
 * `copy`. Validating reads with the writer's contract would blank out most of
 * the real data — the reader has to tolerate every shape ever written, and only
 * insist on the fields the UI actually dereferences.
 */
function parseCopy(
  platform: AdUnitPlatform,
  raw: unknown,
): AdUnitCopyByPlatform[AdUnitPlatform] | null {
  const schema = COPY_READ_SCHEMAS[platform];
  if (!schema) return null;
  const parsed = schema.safeParse(raw);
  if (!parsed.success) {
    logger.error('[AdUnitsService] stored ad copy did not match its platform shape', {
      platform,
      issues: parsed.error.issues.slice(0, 3).map((issue) => issue.path.join('.')),
    });
    return null;
  }
  return parsed.data;
}

function readWarnings(row: AdUnitRow): AdUnitWarning[] {
  const fromColumn = Array.isArray(row.warnings) ? (row.warnings as AdUnitWarning[]) : [];
  if (fromColumn.length > 0) return fromColumn;
  const legacy = (row.copy as { warnings?: unknown } | null)?.warnings;
  return Array.isArray(legacy) ? (legacy as AdUnitWarning[]) : [];
}

/**
 * Read the fan-out batch marker off the RAW `copy` column — never off the
 * parsed `AdUnitRecord['copy']`, for the identical reason
 * `setAdUnitCopyVariant`'s own docblock gives: the platform read schemas are
 * not `.strict()`, so a key they do not declare survives on the raw row and
 * is silently absent from the parsed object. Malformed or partial blocks
 * (a stray `fanOut` key left by some other writer, a truncated shape) read
 * as null rather than a half-built record — the same "state, never guess"
 * discipline `parseBriefEvidenceLineage` in `concept-copy/persistence.ts`
 * already applies to a sibling JSON-embedded block.
 */
function readFanOut(rawCopy: unknown): AdUnitFanOut | null {
  if (typeof rawCopy !== 'object' || rawCopy === null) return null;
  const block = (rawCopy as { fanOut?: unknown }).fanOut;
  if (typeof block !== 'object' || block === null) return null;
  const { batchId, position, format } = block as Record<string, unknown>;
  if (typeof batchId !== 'string' || !batchId) return null;
  if (typeof position !== 'number' || !Number.isInteger(position) || position < 0) return null;
  if (format !== 'image' && format !== 'video') return null;
  return { batchId, position, format };
}

/**
 * The ONE place a stored `ad_units` row becomes an `AdUnitRecord`.
 *
 * `BriefWorkspaceService` builds these too (from the `get_brief_workspace`
 * RPC, which returns `to_jsonb(unit)` — the whole row, warnings included). It
 * used to hand-map the fields and cast, which is how it ended up with a
 * different, wrong idea of where warnings live. One builder, one answer.
 */
export function buildAdUnitRecord(row: AdUnitRow): AdUnitRecord {
  const adPlatform = row.ad_platform as AdUnitPlatform;
  const base: AdUnitRecordBase = {
    id: row.id,
    brandKitId: row.brand_kit_id,
    briefId: row.brief_id,
    projectTaskId: row.project_task_id,
    product: row.product,
    offerClass: row.offer_class,
    status: row.status as AdUnitStatus,
    grounding: row.grounding as AdUnitGrounding,
    warnings: readWarnings(row),
    // `?? null` rather than a bare read: `BriefWorkspaceService` builds records
    // from `to_jsonb(unit)`, and a row written before this column existed simply
    // has no key. `undefined` would sail through a `string | null` field and
    // make "not loaded" indistinguishable from "genuinely unset".
    audienceId: row.audience_id ?? null,
    hypothesis: row.hypothesis ?? null,
    reviewedBy: row.reviewed_by,
    reviewedAt: row.reviewed_at,
    reviewNote: row.review_note,
    createdAt: row.created_at,
    referenceAd: row.reference_ad_id
      ? {
          id: row.reference_ad_id,
          pageId: row.reference_ad_page_id ?? null,
          advertiser: row.reference_ad_advertiser ?? null,
        }
      : null,
    conceptCreativeId: row.concept_creative_id ?? null,
    videoJobId: row.video_job_id ?? null,
    fanOut: readFanOut(row.copy),
  };
  return {
    ...base,
    adPlatform,
    copy: parseCopy(adPlatform, row.copy),
  } as AdUnitRecord;
}

/**
 * The columns an `AdUnitRow` is made of, in one place.
 *
 * It was typed out at each call site and asserted verbatim in the tests, so
 * adding a column meant editing the same string three times and the suite
 * failed on the copy that was forgotten rather than on anything real. Exported
 * so a test can assert against the constant instead of a transcription of it.
 */
export const AD_UNIT_COLUMNS =
  'id, brand_kit_id, brief_id, project_task_id, product, ad_platform, offer_class, status, copy, grounding, warnings, audience_id, hypothesis, reviewed_by, reviewed_at, review_note, created_at, reference_ad_id, reference_ad_page_id, reference_ad_advertiser, concept_creative_id, video_job_id';

export async function listAdUnits(
  ownerId: string,
  brandKitId: string,
  briefId?: string,
): Promise<AdUnitRecord[]> {
  let query = supabase
    .from('ad_units')
    .select(AD_UNIT_COLUMNS)
    .eq('user_id', ownerId)
    .eq('brand_kit_id', brandKitId);
  if (briefId) query = query.eq('brief_id', briefId);
  const { data, error } = await query.order('created_at', { ascending: false });
  if (error) {
    logger.error('[AdUnitsService.listAdUnits] query failed', error);
    throw new Error(error.message);
  }
  return (data ?? []).map((row) => buildAdUnitRecord(row as AdUnitRow));
}

/**
 * One `ad_units` row by id, owner + workspace scoped. Added for the Google Search
 * campaign builder's client-side RSA prefill (`GoogleAdUnitPrefill.tsx`):
 * unlike Meta's builder, which only ever learns a generated ad's
 * fields from the SERVER-stamped `adUnitSnapshot` returned after Save, the
 * Google wizard's headline/description inputs must be pre-filled BEFORE any
 * save happens, so it needs a real read here rather than `listAdUnits`'
 * whole-collection scan. `null` covers both "no such row" and "not this
 * owner's row" identically — RLS already refuses the second case, this just
 * keeps the caller from having to tell the difference.
 */
export async function getAdUnit(
  ownerId: string,
  brandKitId: string,
  adUnitId: string,
): Promise<AdUnitRecord | null> {
  const { data, error } = await supabase
    .from('ad_units')
    .select(AD_UNIT_COLUMNS)
    .eq('id', adUnitId)
    .eq('user_id', ownerId)
    .eq('brand_kit_id', brandKitId)
    .maybeSingle();
  if (error) {
    logger.error('[AdUnitsService.getAdUnit] query failed', error);
    throw new Error(error.message);
  }
  return data ? buildAdUnitRecord(data as AdUnitRow) : null;
}

/**
 * One ad-copy draft that names a competitor reference but has never been
 * attached to a Brief Concept — the exact "Draft ad copy" orphan
 * `docs/discover/ADS.md` documents as an intentional possibility (Discover
 * has no Brief in scope) and this repo's ad-journey audit found nobody was
 * ever told about. `id` is all a caller needs to hand back to
 * {@link attachAdUnitToConcept}; the rest is display copy for "which draft is
 * this" when more than one exists (e.g. a Meta AND a LinkedIn draft from the
 * same reference ad).
 */
export interface UnattachedReferenceAdCopy {
  id: string;
  brandKitId: string;
  adPlatform: AdUnitPlatform;
  product: string;
  createdAt: string;
}

/**
 * Raw `ad_units` rows, at the exact narrow shape
 * `buildPaidActivityFeed` (`paid-media/paid-activity-feed.ts`) reasons
 * about — id, brief_id, ad_platform, product, created_at, source — for its
 * "Generated N ad copy variants" activity entries.
 *
 * Deliberately NOT `listAdUnits` + a re-shape: `AdUnitRecord` never carries
 * `source` (`ad_units_source_check`'s `'chat' | 'pipeline' | 'manual'`) at
 * all — nothing on `AdUnitRecordBase` reads it, because no UI surface before
 * this one needed to distinguish "Mora composed this from a brief" from "Mora
 * mechanically reshaped an existing ad's copy for another platform" (see
 * `AD_UNIT_SOURCE_VERB` in that module). Running every row through
 * `buildAdUnitRecord`'s full parse — copy-schema validation, warnings
 * fallback, fan-out reads — to reach five columns an activity feed needs and
 * discard the rest would be real, needless work for a list that may show
 * every ad-copy row this account has ever generated.
 */
export async function listAdUnitActivityRows(
  ownerId: string,
  brandKitId: string,
): Promise<
  Array<
    Pick<AdUnitRow, 'id' | 'brief_id' | 'ad_platform' | 'product' | 'created_at'> & {
      source: string;
    }
  >
> {
  const { data, error } = await supabase
    .from('ad_units')
    .select('id, brief_id, ad_platform, product, created_at, source')
    .eq('user_id', ownerId)
    .eq('brand_kit_id', brandKitId)
    .order('created_at', { ascending: false });
  if (error) {
    logger.error('[AdUnitsService.listAdUnitActivityRows] query failed', error);
    throw new Error(error.message);
  }
  return (data ?? []) as Array<
    Pick<AdUnitRow, 'id' | 'brief_id' | 'ad_platform' | 'product' | 'created_at'> & {
      source: string;
    }
  >;
}

/**
 * Every ad-copy row the caller owns that was drafted FROM this exact
 * competitor reference ad and still carries no Brief/Concept lineage.
 *
 * Scoped on `reference_ad_id` (never a client-supplied `adUnitId` list) so a
 * caller can ask "did I already draft from this ad?" from anywhere that only
 * knows the reference ad's provider id — `AdDetailPanel`/`AdCard` on
 * `/discover/ads`, which have no memory of which chat turn produced which
 * `ad_units.id`. `.is('brief_id', null).is('project_task_id', null)` is the
 * same "unattached" definition `generate-ad-copy.ts`'s fallback insert path
 * writes and `attachAdUnitToConcept` below refuses to touch anything else.
 */
export async function listUnattachedReferenceAdCopy(
  ownerId: string,
  brandKitId: string,
  referenceAdId: string,
): Promise<UnattachedReferenceAdCopy[]> {
  const { data, error } = await supabase
    .from('ad_units')
    .select('id, brand_kit_id, ad_platform, product, created_at')
    .eq('user_id', ownerId)
    .eq('brand_kit_id', brandKitId)
    .eq('reference_ad_id', referenceAdId)
    .is('brief_id', null)
    .is('project_task_id', null)
    .order('created_at', { ascending: false });
  if (error) {
    logger.error('[AdUnitsService.listUnattachedReferenceAdCopy] query failed', error);
    throw new Error(error.message);
  }
  return (data ?? []).map((row) => ({
    id: row.id as string,
    brandKitId: row.brand_kit_id as string,
    adPlatform: row.ad_platform as AdUnitPlatform,
    product: row.product as string,
    createdAt: row.created_at as string,
  }));
}

/**
 * Attach one previously-orphaned ad-copy draft to a Brief Concept after the
 * fact — the retroactive half of "the UI must say the output is unattached
 * and offer to attach it, never silently write NULL and never invent an
 * association." A plain `update`, gated the same way `setAdUnitHypothesis`
 * above is (owner-scoped RLS + the topology triggers), with one addition:
 * `.is('brief_id', null).is('project_task_id', null)` in the WHERE clause
 * means this can only CREATE a lineage a row does not have yet — it is
 * structurally unable to overwrite an existing attachment, so it can never
 * invent or silently replace an association. The actual Brief/Concept/owner
 * membership check is `assert_ad_unit_brief_concept_membership`
 * (`ad_units_brief_concept_membership_guard`, verified live against
 * `apegsjpotzvxumdedwty` 2026-08-28: it validates the NEW `brief_id`/
 * `project_task_id` pair regardless of the row's previous state, so an
 * unattached → attached transition is exactly the case it exists to allow)
 * — this function does not re-derive that logic, it just surfaces the
 * server's verdict as a normal failed-save error.
 */
export async function attachAdUnitToConcept(
  adUnitId: string,
  brandKitId: string,
  briefId: string,
  projectTaskId: string,
): Promise<AdUnitRecord> {
  const { data, error } = await supabase
    .from('ad_units')
    .update({ brief_id: briefId, project_task_id: projectTaskId })
    .eq('id', adUnitId)
    .eq('brand_kit_id', brandKitId)
    .is('brief_id', null)
    .is('project_task_id', null)
    .select(AD_UNIT_COLUMNS)
    .maybeSingle();
  if (error) {
    logger.error('[AdUnitsService.attachAdUnitToConcept] update failed', error);
    throw new Error(error.message);
  }
  if (!data) {
    // Same RLS/guard shape as `setAdUnitHypothesis`: a forbidden row, a
    // deleted row, a foreign Concept, or a row that is already attached are
    // all filtered out rather than raised — "no error and no row" is what
    // every one of those looks like, so report a failed attach rather than a
    // silent no-op that would look like success.
    throw new Error(
      'That draft could not be attached. It may already be attached, or the Brief/Concept may no longer be reachable.',
    );
  }
  return buildAdUnitRecord(data as AdUnitRow);
}

export async function reviewAdUnit(input: ReviewAdUnitInput): Promise<AdUnitRecord> {
  const { data, error } = await supabase.rpc('transition_ad_unit_review', {
    p_ad_unit_id: input.adUnitId,
    p_status: input.status,
    ...(input.reviewNote === undefined ? {} : { p_review_note: input.reviewNote }),
  });
  if (error) {
    logger.error('[AdUnitsService.reviewAdUnit] transition failed', error);
    throw new Error(error.message);
  }
  return buildAdUnitRecord(data as AdUnitRow);
}

/**
 * Record — or clear — the author's hypothesis for one ad unit.
 *
 * A plain `update`, not an RPC. `ad_units` carries a single owner-scoped RLS
 * policy (`user_id = current_account_owner_id()`) plus BEFORE triggers that
 * reject a mutation under an archived Brief and take the topology advisory
 * lock; none of them need a SECURITY DEFINER wrapper for a column that is
 * neither part of the review state machine nor part of the concept topology.
 * An empty string is stored as null so "typed nothing" and "never wrote one"
 * do not become two states that render identically but compare differently.
 */
export async function setAdUnitHypothesis(
  adUnitId: string,
  brandKitId: string,
  hypothesis: string,
): Promise<AdUnitRecord> {
  const trimmed = hypothesis.trim();
  const { data, error } = await supabase
    .from('ad_units')
    .update({ hypothesis: trimmed || null })
    .eq('id', adUnitId)
    .eq('brand_kit_id', brandKitId)
    .select(AD_UNIT_COLUMNS)
    .maybeSingle();
  if (error) {
    logger.error('[AdUnitsService.setAdUnitHypothesis] update failed', error);
    throw new Error(error.message);
  }
  if (!data) {
    // RLS filters a forbidden row out rather than raising, so "no error and no
    // row" is the shape an unauthorized or deleted write comes back in. Silently
    // returning would report a save that never happened.
    throw new Error('That ad copy could not be updated. Reload the Brief and try again.');
  }
  return buildAdUnitRecord(data as AdUnitRow);
}

/**
 * Attach — or detach (`conceptCreativeId: null`) — the Concept holding this
 * ad's creative. A plain `update`, not an RPC: `concept_creative_id` sits
 * outside `ad_units_concept_topology_lock`'s guarded column list (same
 * reasoning as `setAdUnitHypothesis`), and the account-ownership check that
 * DOES matter here is already enforced server-side by the
 * `ad_units_concept_creative_owner_guard` trigger
 * (`assert_ad_unit_concept_creative_owner`) — a client cannot link to another
 * account's Concept regardless of what this function is told to send.
 */
export async function setAdUnitConceptCreative(
  adUnitId: string,
  brandKitId: string,
  conceptCreativeId: string | null,
): Promise<AdUnitRecord> {
  const { data, error } = await supabase
    .from('ad_units')
    .update({ concept_creative_id: conceptCreativeId })
    .eq('id', adUnitId)
    .eq('brand_kit_id', brandKitId)
    .select(AD_UNIT_COLUMNS)
    .maybeSingle();
  if (error) {
    logger.error('[AdUnitsService.setAdUnitConceptCreative] update failed', error);
    throw new Error(error.message);
  }
  if (!data) {
    // Same RLS shape as `setAdUnitHypothesis`: a forbidden/deleted row is
    // filtered out rather than raised, so "no error and no row" is what an
    // unauthorized write looks like — report it as a failed save, not a
    // silent no-op.
    throw new Error('That ad copy could not be updated. Reload the Brief and try again.');
  }
  return buildAdUnitRecord(data as AdUnitRow);
}

/**
 * Replace the text of ONE variant inside one ad unit's copy.
 *
 * A plain `update`, for the same reason `setAdUnitHypothesis` above is one, and
 * verified rather than assumed: `copy` appears in NO trigger's `UPDATE OF`
 * column list (checked against `pg_trigger` on 2026-08-27 — only
 * `brief_id, project_task_id, user_id, audience_id` are topology-locked, and
 * `concept_creative_id` has its own owner guard), and `authenticated` holds a
 * COLUMN-level UPDATE grant that includes `copy`.
 *
 * ⚠️ That column-level grant is the part worth knowing about, because the
 * table-level check flatly contradicts it:
 * `has_table_privilege('authenticated','ad_units','UPDATE')` returns **false**,
 * and `information_schema.table_privileges` lists only `SELECT, DELETE`. Both
 * are true and neither is the whole answer — the grant lives in
 * `information_schema.column_privileges`. Reading only the table-level answer
 * would have concluded this write was impossible and that the Save-hypothesis
 * button beside it had been broken for weeks; exercising the real button proved
 * otherwise. Check `column_privileges` before declaring an `ad_units` write
 * ungranted.
 *
 * Not every column is covered. `status`, `reviewed_by`, `reviewed_at` and
 * `review_note` are absent from that grant on purpose — review state moves only
 * through `transition_ad_unit_review`, which is why `reviewAdUnit` is an RPC
 * and this is not.
 */
export async function setAdUnitCopyVariant(
  adUnitId: string,
  brandKitId: string,
  platform: AdUnitPlatform,
  slot: AdCopySlot,
  index: number,
  text: string,
): Promise<AdUnitRecord> {
  // 🔴 RE-READ THE RAW COLUMN. The caller is holding `AdUnitRecord['copy']`,
  // which has already been through `parseCopy` — and a non-`.strict()` zod
  // object silently drops every key it does not declare. Ten Meta rows in
  // production store `offerClass` and `warnings` inside `copy`, neither of which
  // `MetaAdUnitCopyReadSchema` declares, and `readWarnings()` falls back to
  // `copy.warnings` for exactly those rows. Editing a headline off the parsed
  // object would have deleted both. Splicing into the RAW row is what makes an
  // edit to one string an edit to only that string.
  const { data: current, error: readError } = await supabase
    .from('ad_units')
    .select('copy')
    .eq('id', adUnitId)
    .eq('brand_kit_id', brandKitId)
    .maybeSingle();
  if (readError) {
    logger.error('[AdUnitsService.setAdUnitCopyVariant] read-before-write failed', readError);
    throw new Error(readError.message);
  }
  if (!current) {
    throw new Error('That ad copy could not be found. Reload the page and try again.');
  }

  const nextCopy = applyVariantEdit(platform, current.copy, slot, index, text);
  if (!nextCopy) {
    // The stored payload is not the shape this edit assumes. Refusing is the
    // only safe answer — writing a guess would corrupt a provider-shaped column.
    throw new Error('That wording could not be applied to this ad. Reload the page and try again.');
  }

  const { data, error } = await supabase
    .from('ad_units')
    .update({ copy: nextCopy as Json })
    .eq('id', adUnitId)
    .eq('brand_kit_id', brandKitId)
    .select(AD_UNIT_COLUMNS)
    .maybeSingle();
  if (error) {
    logger.error('[AdUnitsService.setAdUnitCopyVariant] update failed', error);
    throw new Error(error.message);
  }
  if (!data) {
    // Same RLS shape as the two writers above: a forbidden or deleted row is
    // filtered out rather than raised, so "no error and no row" is what an
    // unauthorized write looks like. Reporting success here would tell someone
    // their rewritten headline was saved when the row never changed.
    throw new Error('That ad copy could not be updated. Reload the page and try again.');
  }
  return buildAdUnitRecord(data as AdUnitRow);
}

/**
 * A platform × format combination "Generate content" can create.
 *
 * `AdUnitFanOut` minus `batchId`/`position` — those are assigned once per
 * batch by `createAdUnitContentFanOut`, not chosen per-combo, so this shape
 * is what a caller actually selects. Reused verbatim by the picker UI
 * (`AdUnitGenerateContentControl.tsx`) and the video-retry path
 * (`useAdUnitContentGeneration.ts`) rather than each declaring its own copy.
 */
export interface AdUnitFanOutRequest {
  adPlatform: AdUnitPlatform;
  format: AdUnitFormat;
}

interface AdUnitCoreCopy {
  headline: string;
  body: string;
  cta: string;
  evidence: string[];
}

/**
 * Pull a platform-agnostic headline/body/CTA out of whatever platform shape
 * `unit.copy` actually is, for `createAdUnitContentFanOut` and
 * `buildVideoPromptFromAdUnit` to re-render into a DIFFERENT platform's
 * shape or a video prompt. Returns null — never a half-built triplet — when
 * the source copy is unreadable or carries neither a headline nor a body,
 * matching every other "nothing to generate from" refusal in this file.
 */
function extractCoreCopy(unit: AdUnitRecord): AdUnitCoreCopy | null {
  if (!unit.copy) return null;

  if (unit.adPlatform === 'linkedin') {
    const copy = unit.copy;
    const headline = copy.headline.trim();
    const body = copy.commentary.trim();
    if (!headline && !body) return null;
    return { headline, body, cta: '', evidence: copy.evidence };
  }

  if (unit.adPlatform === 'google_rsa') {
    const copy = unit.copy;
    const headline = copy.headlines[0]?.text.trim() ?? '';
    const body = copy.descriptions[0]?.text.trim() ?? '';
    if (!headline && !body) return null;
    return { headline, body, cta: '', evidence: copy.evidence };
  }

  const copy = unit.copy;
  const headline = copy.headlines[0]?.text.trim() ?? '';
  const body = copy.primaryTexts[0]?.text.trim() ?? '';
  if (!headline && !body) return null;
  return { headline, body, cta: copy.cta.trim(), evidence: copy.evidence };
}

function toMetaCopy(core: AdUnitCoreCopy): Record<string, unknown> {
  return {
    primaryTexts: [{ text: core.body || core.headline, shape: 'punchy' }],
    headlines: [{ text: core.headline || core.body, role: 'outcome' }],
    description: '',
    cta: core.cta || 'Learn more',
    evidence: core.evidence,
  };
}

function toLinkedInCopy(core: AdUnitCoreCopy): Record<string, unknown> {
  return {
    commentary: core.body || core.headline,
    headline: core.headline || core.body,
    evidence: core.evidence,
  };
}

const FAN_OUT_PLATFORM_NAME: Record<AdUnitPlatform, string> = {
  meta: 'Meta',
  linkedin: 'LinkedIn',
  google_rsa: 'Google Search',
};

/**
 * A row created by "Generate content" for a DIFFERENT platform than its
 * anchor carries adapted, not independently written, copy — `evidence` and
 * `grounding` are the anchor's real ones, but the WORDING was re-rendered
 * into the new platform's field shape by `toMetaCopy`/`toLinkedInCopy`
 * above, not generated or reviewed for that platform. That
 * is a real fact about the row's provenance, so it is recorded the same way
 * every other advisory on this table is — through `warnings`, rendered by
 * `AdUnitGroundingAndAdvisories` in `BriefConceptAdUnits.tsx` — rather than
 * silently presenting adapted copy as equally considered.
 */
function fanOutAdaptationWarning(
  sourcePlatform: AdUnitPlatform,
  targetPlatform: AdUnitPlatform,
): AdUnitWarning | null {
  if (sourcePlatform === targetPlatform) return null;
  return {
    field: 'copy',
    detail: `Adapted automatically from this Concept's ${FAN_OUT_PLATFORM_NAME[sourcePlatform]} ad copy for "Generate content" — written to fit ${FAN_OUT_PLATFORM_NAME[targetPlatform]}'s fields, not independently reviewed for this platform.`,
    provenance: 'Generate content (client-side fan-out)',
  };
}

export interface CreateAdUnitContentFanOutInput {
  ownerId: string;
  briefId: string | null;
  projectTaskId: string | null;
  /** The existing, already-approved-or-drafted ad unit whose copy seeds every row this action creates. */
  anchor: AdUnitRecord;
  requests: AdUnitFanOutRequest[];
}

/**
 * The "Generate content" fan-out writer — one Concept's anchor ad, expanded
 * into sibling `ad_units` rows across a platform × format matrix in a single
 * batched insert.
 *
 * ONLY THE GRANTED COLUMNS ARE WRITTEN. `authenticated`'s INSERT grant on
 * `ad_units` is an explicit column list
 * (`20260810020043_ad_units_review_insert_privileges.sql`:
 * id, user_id, brief_id, angle_id, audience_id, ad_platform, product,
 * offer_class, copy, grounding, warnings, created_at, updated_at,
 * chat_session_id, source, project_task_id) and nothing else — not
 * `concept_copy_command_id`/`concept_copy_position` (see `AdUnitFanOut`'s
 * doc comment), not `concept_creative_id`, not `video_job_id`. This function
 * sends only fields from that list; the video attachment happens in a
 * SEPARATE follow-up write (`setAdUnitVideoJob`, below) once a real
 * `video_jobs` row exists to point at.
 *
 * `source: 'manual'` — the CHECK constraint on this column
 * (`ad_units_source_check`, widened by
 * `20260810125357_brief_copy_command_ledger.sql`) allows `'chat' | 'pipeline'
 * | 'manual'`; this is a direct user click, not a chat turn or a pipeline
 * run, so it is neither of the other two.
 */
export async function createAdUnitContentFanOut(
  input: CreateAdUnitContentFanOutInput,
): Promise<AdUnitRecord[]> {
  if (input.requests.length === 0) return [];

  if (input.requests.some((request) => request.adPlatform === 'google_rsa')) {
    throw new Error(
      'Google ads are written by the ad copy writer, not adapted from another ad. Ask Mora to write the Google copy.',
    );
  }

  const core = extractCoreCopy(input.anchor);
  if (!core) {
    throw new Error("This ad's copy could not be read, so there is nothing to generate from.");
  }

  const batchId = crypto.randomUUID();

  const rows = input.requests.map((request, position) => {
    const copyBase = request.adPlatform === 'linkedin' ? toLinkedInCopy(core) : toMetaCopy(core);

    const fanOut: AdUnitFanOut = { batchId, position, format: request.format };
    const copy = { ...copyBase, fanOut };

    const adaptationWarning = fanOutAdaptationWarning(input.anchor.adPlatform, request.adPlatform);
    const warnings = adaptationWarning ? [adaptationWarning] : [...input.anchor.warnings];

    return {
      id: crypto.randomUUID(),
      user_id: input.ownerId,
      brand_kit_id: input.anchor.brandKitId,
      brief_id: input.briefId,
      audience_id: input.anchor.audienceId,
      ad_platform: request.adPlatform,
      product: input.anchor.product,
      offer_class: input.anchor.offerClass,
      copy: copy as unknown as Json,
      grounding: input.anchor.grounding as unknown as Json,
      warnings: warnings as unknown as Json,
      source: 'manual',
      project_task_id: input.projectTaskId,
    };
  });

  const { data, error } = await supabase.from('ad_units').insert(rows).select(AD_UNIT_COLUMNS);
  if (error) {
    logger.error('[AdUnitsService.createAdUnitContentFanOut] insert failed', error);
    throw new Error(error.message);
  }
  return (data ?? []).map((row) => buildAdUnitRecord(row as AdUnitRow));
}

/**
 * Attach — or detach (`videoJobId: null`) — the `video_jobs` row holding
 * this ad's moving creative. Mirrors `setAdUnitConceptCreative` above:
 * same plain-update shape, same reasoning (`video_job_id` sits outside
 * `ad_units_task7a_content_topology_lock`'s guarded column list, and the
 * cross-account check that matters is enforced server-side by the
 * `ad_units_video_job_owner_guard` trigger regardless of what this function
 * is told to send).
 *
 * ⚠️ UNVERIFIED AT RUNTIME, STATED HONESTLY RATHER THAN ASSUMED. Every other
 * writer in this file that reaches a genuinely new column
 * (`setAdUnitHypothesis` → `hypothesis`, the insert grant → `project_task_id`)
 * has a matching `grant update (…) on public.ad_units to authenticated` or
 * `grant insert (…)` statement in migration history. Searched exhaustively
 * (every `grant …ad_units…` line across `supabase/migrations/*.sql`) and
 * found NONE for `video_job_id` — `20260828020000_ad_units_video_job_link.sql`
 * adds the column, the FK and the owner-guard trigger, but no grant. The
 * identical gap exists for `concept_creative_id`
 * (`setAdUnitConceptCreative`, above), whose own doc comment records that it
 * has shipped with zero callers outside its own test — this exact shape has
 * already gone unverified once. If the grant really is missing, Postgres
 * returns 42501 (permission denied for column), which surfaces here as a
 * thrown `PostgrestError.message` — a real, visible failure, never a silent
 * no-op — so shipping this is safe even if the grant turns out to be
 * missing. Trusting that it SUCCEEDS without exercising the real button once
 * is not safe, and this file cannot do that exercising itself (`execute_sql`
 * and every mutating Supabase MCP tool were out of bounds for the change
 * that added this function).
 */
export async function setAdUnitVideoJob(
  adUnitId: string,
  brandKitId: string,
  videoJobId: string | null,
): Promise<AdUnitRecord> {
  const { data, error } = await supabase
    .from('ad_units')
    .update({ video_job_id: videoJobId })
    .eq('id', adUnitId)
    .eq('brand_kit_id', brandKitId)
    .select(AD_UNIT_COLUMNS)
    .maybeSingle();
  if (error) {
    logger.error('[AdUnitsService.setAdUnitVideoJob] update failed', error);
    throw new Error(error.message);
  }
  if (!data) {
    throw new Error('That ad could not be updated. Reload the Brief and try again.');
  }
  return buildAdUnitRecord(data as AdUnitRow);
}

/**
 * The prompt sent to `POST /api/generate-video` for one ad's video format.
 *
 * Built ONLY from the ad's own stored copy — no invented product fact, no
 * generic stock phrase standing in for one. Null when the copy cannot be
 * read or carries neither headline nor body, the same refusal
 * `createAdUnitContentFanOut` gives for the same reason; the caller (
 * `useAdUnitContentGeneration.ts`) turns that into a visible "could not
 * start" state rather than sending an empty or fabricated prompt.
 */
export function buildVideoPromptFromAdUnit(unit: AdUnitRecord): string | null {
  const core = extractCoreCopy(unit);
  if (!core) return null;
  const line = [core.headline, core.body].filter(Boolean).join(' — ');
  if (!line) return null;
  return `Short-form UGC-style product video for ${unit.product.trim() || 'this product'}. Ground every visual claim in this real ad copy and invent nothing else about the product: "${line}". Natural handheld phone-camera feel, realistic motion, no on-screen text overlays.`;
}

export interface AdCopyPlatformOption {
  /** `ad_units.ad_platform` value this row governs — the FK target. */
  id: AdUnitPlatform;
  label: string;
  /**
   * Whether `select_brief_concept_copy_revision` will accept a candidate on
   * this platform — Meta only as of the 2026-08-26 activation migration
   * (`20260826210000_activate_brief_copy_revision_capability.sql`), which
   * states plainly: "Google RSA and LinkedIn copy cannot be made current and
   * therefore cannot carry paid lineage yet. That is a product gap, not a
   * bug." A Concept whose paid platform resolves to `false` here can have
   * copy written and approved, but that copy cannot yet become the Concept's
   * current pointer or travel downstream to a paid delivery/readout.
   */
  supportsCopyRevision: boolean;
  /** The registry's own one-line reason, shown wherever a UI needs to say WHY. */
  notes: string | null;
}

/**
 * The declared set of ad-copy platforms — `public.ad_copy_platforms`, a
 * global reference table (`ad_copy_platforms_read` grants `SELECT` to every
 * `authenticated` role, `qual: true`; no `user_id` column, so this is NOT
 * account-scoped and never goes through `resolveOwnerId`). `ad_units.ad_platform`
 * carries a FOREIGN KEY to this table (`src/lib/platforms/README.md` item
 * #13), so this list — not a literal in a component — is the one place a
 * platform filter can read "which platforms exist" without drifting from
 * what the database will actually accept.
 *
 * Ordered by `ad_platform` for a stable, deterministic render — the table
 * carries no explicit sort column, and three rows sharing one migration
 * timestamp make `created_at` non-deterministic.
 */
export async function listAdCopyPlatforms(): Promise<AdCopyPlatformOption[]> {
  const { data, error } = await supabase
    .from('ad_copy_platforms')
    .select('ad_platform, label, supports_copy_revision, notes')
    .order('ad_platform', { ascending: true });
  if (error) {
    logger.error('[AdUnitsService.listAdCopyPlatforms] query failed', error);
    throw new Error(error.message);
  }
  return (data ?? []).map((row) => ({
    id: row.ad_platform as AdUnitPlatform,
    label: row.label as string,
    supportsCopyRevision: Boolean(row.supports_copy_revision),
    notes: (row.notes as string | null) ?? null,
  }));
}

export const adUnitsService = {
  listAdUnits,
  listAdUnitActivityRows,
  getAdUnit,
  reviewAdUnit,
  setAdUnitHypothesis,
  setAdUnitConceptCreative,
  setAdUnitCopyVariant,
  listAdCopyPlatforms,
  createAdUnitContentFanOut,
  setAdUnitVideoJob,
  listUnattachedReferenceAdCopy,
  attachAdUnitToConcept,
};
